import React from "react";
import { createRoot } from "react-dom/client";
import { LanguageProvider, ThemeDrive } from "@geotab/zenith";
import type { TSupportedLanguage } from "@geotab/zenith/dist/utils/localization/getSupportedLanguage";
import type AppView from "../../../../app.view";
import * as logger from "../../../../utils/logger";

export type TTranslate = (sentence: string) => string;

/** Keeps back on the current page by adding a copy of it to the history, which is removed again once released. */
function holdHistory(): () => Promise<void> {
    const stay = () => window.history.pushState(window.history.state, "", window.location.href);
    stay();
    window.addEventListener("popstate", stay);

    return () => new Promise<void>(resolve => {
        window.removeEventListener("popstate", stay);
        window.addEventListener("popstate", () => resolve(), { once: true });
        window.history.back();
    });
}

function makeBackgroundInert(container: HTMLElement): () => void {
    const isBackground = (element: Element) => element !== container && !element.hasAttribute("inert");
    const background = Array.prototype.filter.call(document.body.children, isBackground) as Element[];
    background.forEach(element => element.setAttribute("inert", ""));

    return () => background.forEach(element => element.removeAttribute("inert"));
}

/**
 * Renders in its own root on top of the page until answered, and answering is the only way on.
 * A render failure is traced and answered with the fallback, so login never waits forever.
 */
export function renderUntilAnswered<T>(
    app: AppView,
    name: string,
    fallback: T,
    render: (answer: (value: T) => void, translate: TTranslate) => React.ReactNode
): Promise<T> {
    const container = document.body.appendChild(document.createElement("div"));
    const releaseHistory = holdHistory();
    const releaseBackground = makeBackgroundInert(container);

    return new Promise<T>((resolve, reject) => {
        const translator = app.translationService.getTranslator();
        const root = createRoot(container, { onUncaughtError: reject });
        const answer = (value: T) => {
            root.unmount();
            container.remove();
            resolve(value);
        };

        root.render(
            <LanguageProvider language={app.translationService.getLanguage() as TSupportedLanguage}>
                <ThemeDrive dark={app.store.getState().data.nightMode}>
                    {render(answer, sentence => translator.getLocalTranslation(sentence))}
                </ThemeDrive>
            </LanguageProvider>
        );
    }).catch(error => {
        container.remove();
        logger.error(logger.Source.USER, `${ name } failed to render: ${ String(error) }`, app.userService.getCurrentUser());
        return fallback;
    }).then(async value => {
        releaseBackground();
        await releaseHistory();
        return value;
    });
}
